import React from 'react';
import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from 'next-seo';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import BlogLink from 'components/blog/BlogLink';
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/national-tree-week-update`;

  return (
    <MainLayout additionalClassName="blogPost">
      <NextSeo
        title="National Tree Week: An Update from DAYMADE | Win Epic Prizes | DAYMADE"
        description="Let’s celebrate the heroes of nature this National Tree Week. Discover our work with the Eden Reforestation Project and our hopes to give back more in 2023. Enter now to plant your tree."
        openGraph={{
          title: 'National Tree Week Update | Win Epic Prizes | DAYMADE',
          description:
            ' Let’s celebrate the heroes of nature this National Tree Week. Discover our work with the Eden Reforestation Project and our hopes to give back more in 2023. Enter now to plant your tree.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/national-tree-week-update',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'A girl holding a tree in her hands',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'national tree week,plant trees',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="A girl holding a tree in her hands"
        heroImageYPosition="0%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            National Tree Week: An Update from DAYMADE
          </h1>

          <p className="subheading">Let’s celebrate the heroes of nature</p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Violet Currey
          </p>

          <p>
            Happy National Tree Week, everyone! From the 26th of November until
            the 4th of December, it’s officially time to celebrate the Winter
            tree planting season. This week reminds us how important these
            heroes of nature are for our lives, and we should take some time to
            explore the great outdoors around us. Want to get involved with the
            celebrations? Kick off your tree planting party by getting involved
            with local projects, and you can even plant some trees in your
            garden. Have a busy schedule with Christmas just around the corner,
            or don't have any green space in your area? Don’t worry, it's
            National Tree Week every week at DAYMADE, and we're busy planting
            trees with Eden Reforestation Project since 2021. We’ve planted a
            whopping 548,291 trees to date. So you’re not just having some fun
            by winning our epic holiday, lifestyle, tech and experiences prizes,
            you’re also helping the planet along the way. Discover how you can
            celebrate National Tree Week with DAYMADE, and our sustainability
            tree planting plans for 2023.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt="A local farmer planting trees"
            className="singleBlogImage"
          />

          <h2>How to celebrate National Tree Week with DAYMADE</h2>

          <p>
            We’re passionate about future-proofing our world by supporting
            causes dedicated to reversing environmental damage. In this day and
            age, we all know that the future of our environment, wildlife, and
            ecosystems needs our help more than ever. That's why we’ve partnered
            with the Eden Reforestation Project to plant a tree with every
            purchase. We would love everyone to get outside to plant trees
            locally, but we understand that’s easier said than done. By playing
            our weekly draws, you’re not only in for a chance to win some epic
            prizes, but you can contribute like many of our players who have
            planted 548,291 trees so far. It feels good to win, but as part of
            the DAYMADE community, you’re giving the planet a win too. Our prize
            partners are just as tree mad as we are, and many of them plant a
            tree with every purchase, so you're doubling your positive impact
            (feels good right?). With all of our travel prizes, we offset the
            trips through Here's an example of an{' '}
            <BlogLink external="https://daymade.c-level.earth/Flight/">
              C-Level
            </BlogLink>{' '}
            so your next holiday is sustainable, and your trees planted are
            still making a difference. To learn more about our tree planting
            contributions,{' '}
            <BlogLink internal="/blog/7-questions-for-trees-for-the-future">
              read our interview
            </BlogLink>{' '}
            with Brandy Lellou, director of Trees For the Future to understand
            why we need to protect our planet.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="Landscape at Annapurna Circuit, Nepal"
            className="singleBlogImage"
          />

          <h2>2022 Update: Our Work With The Eden Reforestation Project</h2>

          <p>
            We’re excited to announce an update on our work with the Eden
            Reforestation Project: we’ve planted 248,294 trees so far in 2022
            alone. The fantastic work of our players has contributed to cleaning
            our air, reversing environmental damage, and supporting wildlife. It
            has created opportunities for local communities. The Eden
            Reforestation Project is dedicated to rebuilding and preserving the
            natural wilderness in 9 countries, including Nepal, Kenya, and Haiti
            to contribute to making our world a better place. This week is about
            taking a moment to appreciate the power of trees as they do so much
            for us, such as cooling, cleaning and giving us fresh air to breathe
            every day. The Eden Reforestation Project tree planting programs
            support communities that have suffered from the damages to their
            local areas from deforestation and natural disasters caused by
            climate change. Our contributions have not only restored our
            forests, but we’re empowering strong communities with employment and
            education opportunities. This allows local farmers, women and
            children to have a safe future in a protected world. By{' '}
            <BlogLink internal="/giving-back">giving back</BlogLink> through The
            Eden Reforestation Project, we’re helping to overcome the challenges
            that our planet faces by planting one tree at a time.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
            alt="A group of workers at The Eden Reforestation Project"
            className="singleBlogImage"
          />

          <h2>Our Plans to Give Back in 2023</h2>

          <p>
            With 2023 around the corner, our commitment to sustainability and
            helping our planet is a strong goal for next year. We plan to
            continue our work with Eden Reforestation by planting more trees
            with our weekly draws. By introducing our second draw on Tuesdays,
            we’ve allowed our players to double their tree contribution. The
            DAYMADE team wants to do more behind the scenes and play our part.
            That’s why we plan to continue working with Here's an example of an{' '}
            <BlogLink external="https://ecologi.com/thtravelltd">
              Offset Earth
            </BlogLink>{' '}
            to remove more greenhouse gases than we’re putting out. Even though
            we’re offsetting our carbon on our travel prizes, we want to
            introduce more low-carbon ways to travel. We plan to add more train
            travel trips to European destinations such as Paris and Amsterdam as
            well as interrailing trips around Europe. The bucket list-worthy
            destinations are a little tricky to reach by train, so we work
            closely with{' '}
            <BlogLink internal="/blog/best-4-sustainable-travel-companies-2022">
              sustainable travel partners
            </BlogLink>{' '}
            such as Much Better Adventures, who have sustainability at the heart
            of their travels. We plan to grow partnerships with like-minded
            businesses for our prize offerings, from eco-friendly beauty prizes
            to sustainable holiday prizes. That way, we’re helping our players
            make more positive contributions with their winnings. Are you a
            sustainable travel company? Please get in{' '}
            <a href="mailto:lisa@daymade.co.uk?subject=Travel Partnership">
              touch with us.
            </a>
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt="A girl jumping on top of a mountain"
            className="singleBlogImage"
          />

          <Separator style={{ margin: '1em 0 2.5em' }} />

          <p>
            Our sustainability goals to future proof our planet and help
            preserve nature are the heart of what we do at DAYMADE. Our 548,291
            trees planted couldn’t be done without our players, and we hope to
            continue growing the heroes of nature for future generations. You
            can join in by entering our weekly draw each week to win a
            sustainable prize so you're making yourself and the planet a lot
            happier. Let’s celebrate our tree friends that help us grow a
            greener future. Plant your tree for National Tree Week today by
            entering our Weekly Draw.
          </p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1em 0 1.5em' }}
          >
            Plant A Tree
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
